/* eslint-disable max-len */
import {
  Component,
  Host,
  h,
  Element,
  Listen,
  Prop,
  Watch,
} from '@stencil/core';
import {
  computePosition,
  offset,
  Placement,
  flip,
  shift,
  limitShift,
  arrow,
} from '@floating-ui/dom';
import { versionComponent } from '../../global/inject-dependencies';

/**
 * @slot trigger - Content which will be rendered as tooltip trigger
 * @slot default - Content of tooltip
 */

@Component({
  tag: 'ui-tooltip',
  styleUrl: 'ui-tooltip.scss',
  shadow: true,
})
export class UiTooltip {
  @Element() el: HTMLElement;

  /**
   * When set to true, the tooltip component is displayed, otherwise it is hidden. The default value is *false*.
   */
  @Prop({ reflect: true, mutable: true }) open = false;

  @Prop({ reflect: true }) placement: Placement = 'top';

  trigger: HTMLElement;

  tooltip: HTMLElement;

  arrow: HTMLElement;

  @Watch('open')
  openChangeHandler(newValue: boolean) {
    if (newValue) {
      this.positionTooltip();
    }
  }

  @Listen('click', { target: 'document' })
  handleClick(e) {
    const tooltipIsTarget = e
      .composedPath()
      .some((element) => element === this.el);
    if (!tooltipIsTarget) {
      this.open = false;
    }
  }

  onIconClick() {
    this.open = !this.open;
    if (this.open) {
      this.positionTooltip();
    }
  }

  positionTooltip() {
    computePosition(this.trigger, this.tooltip, {
      placement: this.placement,
      middleware: [
        offset(20),
        flip(),
        shift({ limiter: limitShift() }),
        arrow({
          element: this.arrow,
        }),
      ],
    }).then(({ x, y, middlewareData }) => {
      if (middlewareData.arrow) {
        const arrowX = middlewareData.arrow.x;
        const arrowY = middlewareData.arrow.y;
        Object.assign(this.arrow.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY}px` : '',
        });
      }
      Object.assign(this.tooltip.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  }

  componentWillLoad() {
    this.openChangeHandler(this.open);
  }

  componentDidLoad() {
    // in case elements gets attribute 'title',
    // we are removing it not to have autogenerated tooltip by browser
    this.el.removeAttribute('title');

    this.positionTooltip();
  }

  renderIconAsTrigger() {
    const slottedTrigger = this.el.querySelector('[slot="trigger"]');
    return !(slottedTrigger && slottedTrigger.innerHTML !== '');
  }

  render() {
    return (
      <Host>
        <div
          ref={(el) => {
            this.trigger = el;
          }}
          class={{
            trigger: true,
            active: this.open,
          }}
          onClick={() => this.onIconClick()}
        >
          <slot name="trigger"></slot>
          {this.renderIconAsTrigger() && (
            <ui-icon
              part="icon"
              class={{ active: this.open }}
              icon="info-circle"
              type="system"
            ></ui-icon>
          )}
        </div>
        <div
          ref={(el) => {
            this.tooltip = el;
          }}
          data-show={this.open}
          data-placement={this.placement}
          class={{
            tooltip: true,
            'tooltip--open': this.open,
          }}
        >
          <div class="tooltip__content">
            <slot></slot>
          </div>
          <div
            class="tooltip__arrow"
            ref={(el) => {
              this.arrow = el;
            }}
          ></div>
        </div>
      </Host>
    );
  }
}

versionComponent(UiTooltip);
